import { useCallback, useEffect, useRef, useState } from "react";
import { apiService } from "../services/apiService";
import { SchedulePatch } from "../utils/schedulePatch";

export type RealtimeStatus = "live" | "reconnecting" | "offline";

const MAX_BACKOFF_MS = 15_000;
const INITIAL_BACKOFF_MS = 1_000;
const TOKEN_REFRESH_INTERVAL_MS = 50 * 60 * 1000;
// Quick alt-tab / tab switches do not need catch-up while the socket stays
// open. After a longer hide the page may have been frozen and dropped patches.
const CATCH_UP_AFTER_HIDDEN_MS = 60_000;

function buildWebSocketUrl(wsUrl: string, ticket: string): string {
  const url = new URL(wsUrl);
  url.searchParams.set("ticket", ticket);
  return url.toString();
}

function isSchedulePatch(data: unknown): data is SchedulePatch {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as SchedulePatch).type === "schedule.patch" &&
    Array.isArray((data as SchedulePatch).changes)
  );
}

function isTabHidden(): boolean {
  return typeof document !== "undefined" && document.hidden;
}

function isSocketOpen(ws: WebSocket | null): boolean {
  return ws?.readyState === WebSocket.OPEN;
}

export function useScheduleRealtime(
  isApiReady: boolean,
  onPatch: (patch: SchedulePatch) => void,
  onReconnect?: () => void,
): { status: RealtimeStatus } {
  const [status, setStatus] = useState<RealtimeStatus>("offline");
  const onPatchRef = useRef(onPatch);
  const onReconnectRef = useRef(onReconnect);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tokenRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const backoffRef = useRef(INITIAL_BACKOFF_MS);
  const mountedRef = useRef(true);
  const connectingRef = useRef(false);
  const pendingTokenRefreshRef = useRef(false);
  const hiddenAtRef = useRef<number | null>(null);

  useEffect(() => {
    onPatchRef.current = onPatch;
  }, [onPatch]);

  useEffect(() => {
    onReconnectRef.current = onReconnect;
  }, [onReconnect]);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const clearTimers = useCallback(() => {
    clearReconnectTimer();
    if (tokenRefreshTimerRef.current) {
      clearTimeout(tokenRefreshTimerRef.current);
      tokenRefreshTimerRef.current = null;
    }
  }, [clearReconnectTimer]);

  const closeSocket = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onmessage = null;
      wsRef.current.onerror = null;
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    const scheduleReconnect = (delayMs?: number) => {
      if (!mountedRef.current || !isApiReady) {
        return;
      }
      if (isTabHidden()) {
        clearReconnectTimer();
        setStatus("offline");
        return;
      }
      clearTimers();
      const delay = delayMs ?? backoffRef.current;
      setStatus("reconnecting");
      reconnectTimerRef.current = setTimeout(() => {
        connect();
      }, delay);
      backoffRef.current = Math.min(backoffRef.current * 2, MAX_BACKOFF_MS);
    };

    const scheduleTokenRefreshReconnect = () => {
      if (!mountedRef.current) {
        return;
      }
      tokenRefreshTimerRef.current = setTimeout(() => {
        if (isTabHidden()) {
          pendingTokenRefreshRef.current = true;
          return;
        }
        closeSocket();
        backoffRef.current = INITIAL_BACKOFF_MS;
        scheduleReconnect(0);
      }, TOKEN_REFRESH_INTERVAL_MS);
    };

    const connect = async () => {
      if (!mountedRef.current || !isApiReady || connectingRef.current) {
        return;
      }
      if (isTabHidden()) {
        setStatus("offline");
        return;
      }

      connectingRef.current = true;
      clearTimers();
      closeSocket();

      try {
        const response = await apiService.createRealtimeTicket();
        if (!mountedRef.current || !isApiReady) {
          return;
        }
        if (isTabHidden()) {
          setStatus("offline");
          return;
        }

        if (
          !response.success ||
          !response.data?.ticket ||
          !response.data?.ws_url
        ) {
          setStatus("offline");
          scheduleReconnect();
          return;
        }

        const ws = new WebSocket(
          buildWebSocketUrl(response.data.ws_url, response.data.ticket),
        );
        wsRef.current = ws;

        ws.onopen = () => {
          if (!mountedRef.current) {
            return;
          }
          pendingTokenRefreshRef.current = false;
          backoffRef.current = INITIAL_BACKOFF_MS;
          setStatus("live");
          scheduleTokenRefreshReconnect();
          onReconnectRef.current?.();
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data as string);
            if (isSchedulePatch(data)) {
              onPatchRef.current(data);
            }
          } catch {
            // Ignore malformed messages
          }
        };

        ws.onerror = () => {
          if (!mountedRef.current) {
            return;
          }
          if (!isTabHidden()) {
            setStatus("reconnecting");
          }
        };

        ws.onclose = () => {
          if (!mountedRef.current) {
            return;
          }
          wsRef.current = null;
          if (isApiReady) {
            scheduleReconnect();
          } else {
            setStatus("offline");
          }
        };
      } catch {
        if (mountedRef.current) {
          setStatus("offline");
          scheduleReconnect();
        }
      } finally {
        connectingRef.current = false;
      }
    };

    const handleVisibilityChange = () => {
      if (!mountedRef.current || !isApiReady) {
        return;
      }

      if (isTabHidden()) {
        hiddenAtRef.current = Date.now();
        clearReconnectTimer();
        if (!isSocketOpen(wsRef.current)) {
          setStatus("offline");
        }
        return;
      }

      const hiddenForMs =
        hiddenAtRef.current === null ? 0 : Date.now() - hiddenAtRef.current;
      hiddenAtRef.current = null;

      if (isSocketOpen(wsRef.current)) {
        if (pendingTokenRefreshRef.current) {
          pendingTokenRefreshRef.current = false;
          closeSocket();
          backoffRef.current = INITIAL_BACKOFF_MS;
          connect();
          return;
        }
        if (hiddenForMs >= CATCH_UP_AFTER_HIDDEN_MS) {
          onReconnectRef.current?.();
        }
        return;
      }

      backoffRef.current = INITIAL_BACKOFF_MS;
      connect();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    if (isApiReady) {
      backoffRef.current = INITIAL_BACKOFF_MS;
      connect();
    } else {
      clearTimers();
      closeSocket();
      setStatus("offline");
    }

    return () => {
      mountedRef.current = false;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearTimers();
      closeSocket();
      connectingRef.current = false;
    };
  }, [isApiReady, clearTimers, clearReconnectTimer, closeSocket]);

  return { status };
};
